/**
 * Event list for the home view and the upload picker.
 *
 * Calls the backend `GET /events` once — the Lambda resolves each event's cover
 * + count server-side in a single S3 pass and filters to the caller's access.
 * This replaces the previous client-side N+1 (a listing call per event folder),
 * so the home page is O(1) API calls regardless of album count.
 */
import { useQuery } from '@tanstack/react-query';
import { getEvents } from '../lib/apiClient.ts';
import { toThumbnailKey } from '../lib/keys.ts';
import type { EventSummary } from '../lib/types.ts';

export function useEvents() {
  return useQuery({
    queryKey: ['events'],
    queryFn: async ({ signal }): Promise<EventSummary[]> => {
      const events = await getEvents(signal);
      return events.map((e) => ({
        name: e.name,
        count: e.count,
        coverThumbnailKey: e.coverThumbnailKey,
        dateFrom: e.dateFrom,
        dateTo: e.dateTo,
      }));
    },
    staleTime: 60_000,
  });
}

/** Cover thumbnail key for an event (already a thumb key from /events). */
export function coverKeyFor(event: EventSummary): string | undefined {
  if (event.coverThumbnailKey) return toThumbnailKey(event.coverThumbnailKey);
  return undefined;
}
